import { atom } from "jotai";
import { AxiosInstance } from "axios";
import useApi, {
	ApiActions,
	ApiStatus,
	emptyApiState,
	Pagination,
} from "src/hooks/api/utils/useApi";
import useForm from "src/hooks/schemes/useForm";
import { useTranslation } from "react-i18next";
import useLogin from "src/hooks/api/services/auth/useLogin";
import { useEffect } from "react";
import useSignupReset from "src/hooks/api/services/auth/useSignupReset";
import useSignupTracking from "src/hooks/api/services/auth/useSignupTracking";
import useLanguage from "src/hooks/api/useLanguage";

interface SignupActions extends ApiActions {
	getField: (name: string) => any;
	setForm: any;
	signup: (data?: any) => Promise<any>;
	validateField: (name: string) => void;
	getInvitation: () => Promise<any>;
}

type State = {
	user: any;
	invite?: any;
	form: any;
	validate: any;
	status: ApiStatus;
	actions: SignupActions;
	api: AxiosInstance;
	pagination: Pagination;
};

const studentAtom: any = atom(emptyApiState);
const formAtom: any = atom({});
const validateAtom: any = atom({});

type Options = {
	type: "student" | "company" | "company-invite";
	filter?: any;
	defaultValue?: any;
	inviteId?: string;
};

function useSignup(options: Options): State {
	const resetState = useSignupReset();
	const { t } = useTranslation("schemes");
	const trackingData = useSignupTracking();
	const { language } = useLanguage();
	const {
		actions: { authenticate },
	} = useLogin();
	const urls = {
		student: "/auth/signup",
		company: "/auth/signup/company",
		"company-invite": `/company-invite/${options?.inviteId}`,
	};

	const { state, actions, api, pagination } = useApi(studentAtom, {
		baseUrl: urls[options.type],
		query: {
			...(options?.filter || {}),
		},
	});
	const {
		form,
		actions: { setForm, getField, setErrors, validateField, resetForm },
		validate,
	} = useForm({
		atoms: {
			form: formAtom,
			validate: validateAtom,
		},
		scheme: {
			first_name: ["string", "required"],
			last_name: ["string", "required"],
			email: ["string", "email", "required"],
			telephone: ["string", "telephone", "required"],
			password: ["string", "password", "required"],
			...(options.type === "company"
				? {
						job_title: ["string", "required"],
				  }
				: {}),
		},
	});

	useEffect(() => {
		if (
			options?.defaultValue &&
			Object.keys(options?.defaultValue).length
		) {
			setForm(options.defaultValue, false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const getInvitation = async () => {
		actions.set((state: any) => ({
			...state,
			status: "loading",
		}));

		try {
			const { data } = await api.get(`/check`).then(({ data }) => data);

			if (data?.email) {
				setForm(
					Object.fromEntries(
						Object.entries({
							first_name: data?.first_name,
							last_name: data?.last_name,
							email: data?.email,
							telephone: data?.telephone,
							job_title: data?.job_title,
						}).filter(([_, v]) => v != null)
					),
					false
				);
			}

			actions.set((state: any) => ({
				...state,
				status: "idle",
				custom: {
					invite: data,
				},
			}));

			return data;
		} catch (error) {
			throw error;
		}
	};

	const signup = async (data?: any) => {
		if (!validate.isValid) return;
		actions.set((state: any) => ({
			...state,
			status: "creating",
		}));

		if (trackingData?.referral_type && data?.company) {
			data.company.referral_type = trackingData.referral_type;
		}
		const formData = {
			...form,
			...(data || {}),
			...trackingData,
			locale: language.current,
		};

		try {
			const user = await (options.type === "company-invite"
				? api.post(`/redeem`, formData).then(({ data }) => data?.data)
				: actions.create(formData));

			await authenticate(user);
			resetState();
			resetForm();
			actions.set((state: any) => ({
				...state,
				status: "idle",
			}));
		} catch (error: any) {
			if (error?.errors?.in_use) {
				setErrors({
					email: t("email-in-use"),
				});
			}
			if (error?.errors?.kvk_in_use) {
				setErrors({
					kvk: t("kvk-in-use"),
				});
			}
			actions.set((state: any) => ({
				...state,
				status: "idle",
			}));
		}
	};

	return {
		user: state?.data || [],
		invite: state?.custom?.invite,
		form,
		validate,
		status: state.status,
		pagination,
		actions: {
			...actions,
			getField,
			validateField,
			setForm,
			signup,
			getInvitation,
		},
		api,
	};
}

export default useSignup;
